import { useCallback, useMemo, useState } from "react";
import type { FloorPlanRoom } from "../../utils/types";
import type {
  UseRoomsMergeSelectionArgs,
  UseRoomsMergeSelectionResult,
} from "./types";

const NO_ROOMS: number[] = [];

export const useRoomsMergeSelection = ({
  rooms,
  isSelecting,
  startSelecting,
  stopSelecting,
  onMergeRooms,
}: UseRoomsMergeSelectionArgs): UseRoomsMergeSelectionResult => {
  const [tickedRoomIds, setTickedRoomIds] = useState<number[]>(NO_ROOMS);
  const [name, setName] = useState("");
  const [isMerging, setIsMerging] = useState(false);

  // Ticks count only while the mode is on, and only for rooms still on the plan.
  const selectedRoomIds = useMemo(() => {
    if (!isSelecting) return null;
    const roomIds = new Set(rooms.map((room) => room.id));
    return tickedRoomIds.filter((roomId) => roomIds.has(roomId));
  }, [isSelecting, rooms, tickedRoomIds]);

  const start = useCallback(() => {
    setTickedRoomIds(NO_ROOMS);
    setName("");
    startSelecting();
  }, [startSelecting]);

  // The first room ticked while the name is empty gives the merged room its name.
  const toggleRoom = useCallback(
    (room: FloorPlanRoom) => {
      if (isMerging || selectedRoomIds === null) return;
      if (selectedRoomIds.includes(room.id)) {
        setTickedRoomIds(
          selectedRoomIds.filter((roomId) => roomId !== room.id),
        );
        return;
      }
      setTickedRoomIds([...selectedRoomIds, room.id]);
      if (name.trim() === "") setName(room.name);
    },
    [isMerging, selectedRoomIds, name],
  );

  const merge = useCallback(async () => {
    if (selectedRoomIds === null) return;
    setIsMerging(true);
    const isMerged = await onMergeRooms({ roomIds: selectedRoomIds, name });
    setIsMerging(false);
    if (isMerged) stopSelecting();
  }, [selectedRoomIds, onMergeRooms, name, stopSelecting]);

  return {
    selectedRoomIds,
    name,
    isMerging,
    start,
    cancel: stopSelecting,
    toggleRoom,
    setName,
    merge,
  };
};
